import React, { useState } from 'react';
import { CreditCard, Loader } from 'lucide-react';
import { API_BASE } from './lib/runtimeConfig';
import { desglose, NOMBRE as NOMBRE_TARIFA } from './tarifaDigital';

const API = API_BASE;
const CHECKOUT_URL = 'https://checkout.wompi.co/p/';

const WompiButton = ({
    amount,
    reference,
    customerEmail    = '',
    customerName     = '',
    customerPhone    = '',
    redirectPath     = '/pago-confirmado',
    label            = 'Pagar en línea con Wompi',
    disabled         = false,
    style            = {},
    onBeforeRedirect = null,   // callback justo antes de navegar a Wompi, recibe la referencia final
    prepare          = null,   // async () => ({ reference, amount }) | null — p. ej. crear la reserva antes de pagar
}) => {
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState('');

    const handlePay = async () => {
        if (!amount || amount <= 0)     { setError('El monto debe ser mayor a cero'); return; }
        if (!reference && !prepare)     { setError('Referencia de pago requerida'); return; }

        setLoading(true); setError('');
        try {
            let finalReference = reference;
            let finalAmount    = amount;
            if (prepare) {
                const prepared = await prepare();
                if (!prepared?.reference) { setLoading(false); return; }
                finalReference = prepared.reference;
                if (prepared.amount) finalAmount = prepared.amount;
            }
            const amountCents = desglose(finalAmount).total * 100;

            // 1. Obtener hash de integridad del servidor
            const r = await fetch(`${API}/api/wompi/integrity`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reference: finalReference, amountCents }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.error || 'Error al generar la firma de pago');

            const { integrity, publicKey } = d;
            if (!publicKey) throw new Error('Llave pública de Wompi no configurada');
            if (!integrity) throw new Error('No se pudo generar la firma de integridad');

            // 2. Enviar via formulario HTML GET — idéntico al ejemplo de Wompi docs
            // Esto garantiza que el browser codifique los parámetros exactamente como
            // Wompi espera (signature:integrity, customer-data:email, etc.)
            // Usar REACT_APP_PUBLIC_URL (tunnel/dominio) si existe, si no window.origin
            const publicOrigin = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
            const isLocalhost = !process.env.REACT_APP_PUBLIC_URL &&
                (publicOrigin.includes('localhost') || publicOrigin.includes('127.0.0.1'));
            // Wompi bloquea redirect-url con localhost — solo enviarlo si hay URL pública
            const redirectUrl = isLocalhost ? null : `${publicOrigin}${redirectPath}`;

            const form = document.createElement('form');
            form.method = 'GET';
            form.action = CHECKOUT_URL;
            form.style.display = 'none';

            const campos = {
                'public-key':          publicKey,
                'currency':            'COP',
                'amount-in-cents':     String(amountCents),
                'reference':           finalReference,
                'signature:integrity': integrity,
            };
            if (redirectUrl) campos['redirect-url'] = redirectUrl;
            if (customerEmail) campos['customer-data:email']               = customerEmail;
            if (customerName)  campos['customer-data:full-name']           = customerName;
            if (customerPhone) {
                const digitos = customerPhone.replace(/\D/g, '');
                campos['customer-data:phone-number']        = digitos.length === 12 && digitos.startsWith('57') ? digitos.slice(2) : digitos;
                campos['customer-data:phone-number-prefix'] = '57';
            }

            Object.entries(campos).forEach(([name, value]) => {
                const input = document.createElement('input');
                input.type  = 'hidden';
                input.name  = name;
                input.value = value;
                form.appendChild(input);
            });

            document.body.appendChild(form);
            if (onBeforeRedirect) onBeforeRedirect(finalReference);
            form.submit();

        } catch (e) {
            setError(e.message);
            setLoading(false);
        }
    };

    const cop = n => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);
    const cobro = desglose(amount);

    return (
        <div>
            {cobro.total > 0 && (
                <div style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: 12, color: '#475569', fontFamily: 'Inter, sans-serif' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span>Valor de la reserva</span><span>{cop(cobro.base)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 4 }}><span>{NOMBRE_TARIFA}</span><span>{cop(cobro.tarifa)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 6, paddingTop: 6, borderTop: '1px solid #e2e8f0', fontWeight: 800, color: '#0f172a' }}><span>Total a pagar</span><span>{cop(cobro.total)}</span></div>
                </div>
            )}
            <button
                onClick={handlePay}
                disabled={disabled || loading || !amount}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    padding: '13px 24px', borderRadius: 12, border: 'none',
                    cursor: (disabled || loading || !amount) ? 'default' : 'pointer',
                    background: (disabled || loading || !amount) ? '#e2e8f0' : 'linear-gradient(135deg,#4B3FD5,#7C3AED)',
                    color: (disabled || loading || !amount) ? '#94a3b8' : '#fff',
                    fontWeight: 800, fontSize: 14, fontFamily: 'Inter, sans-serif',
                    transition: 'all 0.2s', width: '100%',
                    opacity: disabled ? 0.6 : 1,
                    ...style,
                }}
            >
                {loading
                    ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }}/> Redirigiendo a Wompi…</>
                    : <><CreditCard size={16}/> {label}{cobro.total ? ` · ${cop(cobro.total)}` : ''}</>
                }
            </button>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {['Visa','Mastercard','PSE','Nequi','Bancolombia'].map(m => (
                    <span key={m} style={{ fontSize: 10, fontWeight: 700, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '2px 8px', borderRadius: 20 }}>{m}</span>
                ))}
            </div>

            {error && <p style={{ margin: '8px 0 0', fontSize: 12, color: '#dc2626', fontWeight: 600, textAlign: 'center' }}>⚠ {error}</p>}

            <p style={{ margin: '6px 0 0', fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
                Pago seguro procesado por <strong>Wompi</strong>
            </p>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
    );
};

export default WompiButton;
